'use client';

import { NavContainer, PrimaryNav } from '@petsmart-ui/sparky';
import { usePageLayout, useStore, useDecisions } from '@libs/common-next';
import { handleServiceClick } from '@libs/experience-api';
import { dataLayerPush, getBaseUrlJoined } from '@libs/analytics';
import { type MouseEvent, useEffect, useState } from 'react';

import { isUserLoggedIn } from '@libs/core';
import { propsMapper } from '../../utils/propsMapper';
import { LegacyMyStore } from '../LegacyMyStore/LegacyMyStore';
import { SubMenu } from './SubMenu';
import { handleNavLink } from './utils/handleNavLink';

import { type CategoryTypes, type MainMenuProps } from './types';
import { getOptiHeaderText } from '../../utils/getOptiHeaderText';
import './MainMenu.scss';
import './MainMenuLayouts.scss';
import './MainMenuLegacy.scss';
import './MainMenuLegacyLayouts.scss';
import { trackingData } from './utils';

export function MainMenu({ category: menuCategories }: Readonly<MainMenuProps>) {
    const { isAuthenticated } = usePageLayout();
    const { selectedStore } = useStore();
    const { data: store } = selectedStore;

    const [isSubMenuVisible, setIsSubMenuVisible] = useState(false);
    const [menuTexts, setMenuTexts] = useState<Record<string, string>>({});

    // [SB-1434] The navigation header is hard-coded to use
    // `legacy-mainmenu__primary-nav` class to prevent build-time
    // caching issues that cause UI errors (missing dropdown and
    // incorrect white background) during deployments.
    // When we launch the new navigation bar, we will need to pull
    // `flags: { legacyDisplay }` from userPageLayout() and then
    // conditionally render `mainmenu__primary-nav` or
    // `mainmenu__primary-nav` based on the flag's boolean.
    const newDisplay = useDecisions('exp_header_footer');
    const navClasses = newDisplay
        ? {
              container: 'mainmenu__container',
              primaryNav: 'mainmenu__primary-nav',
          }
        : {
              container: 'legacy-mainmenu__container',
              primaryNav: 'legacy-mainmenu__primary-nav',
          };

    if (newDisplay) {
        const itemstoRemove = ['Health & Wellness', 'Find a Store', 'Learning', 'My store', 'Help'];
        const indexesToRemove: number[] = [];
        propsMapper<CategoryTypes>(menuCategories).forEach((menuCategory: CategoryTypes, index) => {
            if (menuCategory.displayText && itemstoRemove.includes(menuCategory.displayText)) {
                indexesToRemove.push(index);
            }
        });
        // Sort indexes in descending order if they aren't already
        indexesToRemove.sort((a, b) => b - a);
        if (menuCategories.length > 6) {
            for (const i of indexesToRemove) {
                menuCategories.splice(i, 1);
            }
            const brandIndex = propsMapper<CategoryTypes>(menuCategories).findIndex(
                menuCategory => menuCategory.displayText === 'Shop by brand'
            );
            const petIndex = propsMapper<CategoryTypes>(menuCategories).findIndex(
                menuCategory => menuCategory.displayText === 'Shop by pet'
            );
            const pharmacyIndex = propsMapper<CategoryTypes>(menuCategories).findIndex(
                menuCategory => menuCategory.displayText === 'Pharmacy' || menuCategory.displayText === 'Made in Canada'
            );
            const dealIndex = propsMapper<CategoryTypes>(menuCategories).findIndex(
                menuCategory => menuCategory.displayText === 'Sale' || menuCategory.displayText === 'Deals'
            );
            [menuCategories[brandIndex], menuCategories[petIndex]] = [
                menuCategories[petIndex],
                menuCategories[brandIndex],
            ];
            if (pharmacyIndex && dealIndex) {
                [menuCategories[dealIndex], menuCategories[pharmacyIndex]] = [
                    menuCategories[pharmacyIndex],
                    menuCategories[dealIndex],
                ];
            }
        }
    }
    const mappedCategories = propsMapper<CategoryTypes>(menuCategories).filter(c => c.sysId);

    useEffect(() => {
        const newMenuTexts: { [key: string]: string } = {};
        console.log('mappedCategories ', mappedCategories);
        mappedCategories.forEach((mappedCategory: CategoryTypes) => {
            const { sysId, type = 'text', displayText } = mappedCategory;
            const isMyStore = type === 'my-store';
            let menuText: string;
            if (!isUserLoggedIn() && isMyStore) {
                menuText = 'Find a Store';
            } else if (newDisplay) {
                menuText = getOptiHeaderText(displayText ?? '');
                const section = document.querySelector('#sale a span') as HTMLElement;
                if (section) {
                    section.style.color = '#DD2834';
                }
            } else {
                menuText = displayText as string;
            }
            // @ts-expect-error need to use ts-ignore to delete error for sysId being string | undefined (There is no possibility for it to be undefined, error is thrown because of Partial type used on CategoryTypes type )
            newMenuTexts[sysId] = menuText;
        });

        setMenuTexts(newMenuTexts);
    }, [isAuthenticated, store]);

    return (
        <NavContainer data-testid="main-navigation" className={navClasses.container}>
            <PrimaryNav
                className={navClasses.primaryNav}
                onMouseEnter={() => setIsSubMenuVisible(true)}
                id="primary-nav"
                data-testid="primary-nav"
            >
                {mappedCategories.map((mappedCategory: CategoryTypes) => {
                    const {
                        sysId,
                        type = 'text',
                        displayText,
                        link,
                        specialType,
                        categories,
                        highlighted,
                        menuDropdownStyle = false,
                    } = mappedCategory;

                    const isMyStore = type === 'my-store';
                    const emptyStore = isMyStore && !store;
                    const currentText =
                        // @ts-expect-error need to use ts-ignore to delete error for sysId being string | undefined (There is no possibility for it to be undefined, error is thrown because of Partial type used on CategoryTypes type )
                        menuTexts[sysId] ?? (newDisplay ? getOptiHeaderText(displayText ?? '') : displayText);

                    const { href, text } = handleNavLink(type, currentText, link, store);

                    function handleOnClick(event: MouseEvent) {
                        dataLayerPush(
                            trackingData({
                                selection: displayText,
                                link_url: getBaseUrlJoined(href),
                            })
                        );

                        handleServiceClick({
                            authType: isAuthenticated,
                            event,
                            link: href,
                        });
                    }

                    return (
                        <PrimaryNav.Item
                            tabIndex={-1}
                            className={'sparky-c-primary-nav__item-padding' + (highlighted ? 'highlighted' : '')}
                            href={href ?? '#'}
                            text={newDisplay && text?.toLowerCase() === 'sale' ? 'Deals' : text}
                            key={sysId}
                            onNavItemClick={handleOnClick}
                            id={displayText?.toLowerCase().split(' ').join('-')}
                        >
                            {
                                // isSubMenuVisible:
                                // This conditional rendering is necessary to ensure that SubMenu Components are only rendered when the user needs them,
                                // this reduces the initial load of the layout and therefore reducing TBT (Performance Metric)
                                !emptyStore && isSubMenuVisible && (
                                    <>
                                        {(categories || type === 'card') && (
                                            <SubMenu
                                                mappedCategory={mappedCategory}
                                                specialType={specialType}
                                                path={mappedCategory.displayText ? [mappedCategory.displayText] : []}
                                                type={type}
                                                parentCategory={displayText}
                                                menuDropdownStyle={menuDropdownStyle}
                                            />
                                        )}
                                        {isMyStore && (
                                            <ul className="submenu-container__list">
                                                <LegacyMyStore />
                                            </ul>
                                        )}
                                    </>
                                )
                            }
                        </PrimaryNav.Item>
                    );
                })}
            </PrimaryNav>
        </NavContainer>
    );
}
